import { useState } from 'react';
import { BrowserRouter, MemoryRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { ThemeProvider } from './context/ThemeContext';
import { AppShell } from './components/layout/AppShell';
import { DeviceFrame } from './components/layout/DeviceFrame';
import { SplashScreen } from './components/shared/SplashScreen';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { OfflineBanner } from './components/shared/OfflineBanner';
import { CalendarTab } from './components/calendar/CalendarTab';
import { Navigate } from 'react-router-dom';
import { FriendsTab } from './components/friends/FriendsTab';
import { ProfileTab } from './components/profile/ProfileTab';
import { MessagesTab } from './components/chat/MessagesTab';
import { LoginScreen } from './components/auth/LoginScreen';
import { SignUpScreen } from './components/auth/SignUpScreen';
import { ProfileSetupScreen } from './components/auth/ProfileSetupScreen';
import { Loader2, Phone, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { supabase } from './lib/supabase';
import { hashPhone, normalizeToE164 } from './utils/phoneHash';
import { saveLocalPhone } from './utils/localPhone';
import { COUNTRY_CODES } from './utils/countryCodes';
import { AddFriendRedirect } from './components/friends/AddFriendRedirect';
import { AddFriendLanding } from './components/friends/AddFriendLanding';

// Capacitor sets window.Capacitor when running as native app
const isNative =
  typeof window !== 'undefined' &&
  !!(window as unknown as Record<string, unknown>).Capacitor;

function AppRouter({ children }: { children: React.ReactNode }) {
  if (isNative) return <MemoryRouter>{children}</MemoryRouter>;
  return <BrowserRouter>{children}</BrowserRouter>;
}

function AuthGate() {
  const { session, user, loading, hasProfile, passwordRecovery, clearPasswordRecovery, updatePassword } = useAuth();
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');

  // Loading auth state
  if (loading) {
    return (
      <div className="min-h-dvh bg-slate-900 flex flex-col">
        <OfflineBanner />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={32} className="text-slate-500 animate-spin" />
        </div>
      </div>
    );
  }

  // Password recovery flow — user clicked reset link
  if (passwordRecovery && session) {
    return <SetNewPasswordScreen updatePassword={updatePassword} onDone={clearPasswordRecovery} />;
  }

  // Not authenticated
  if (!session) {
    // /add/:handle → show landing page with download CTA + deep link attempt
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/add/')) {
      const handle = window.location.pathname.replace('/add/', '').replace(/^@/, '');
      if (handle) {
        localStorage.setItem('rostfound_pending_add', handle);
        return <AddFriendLanding handle={handle} />;
      }
    }
    if (authMode === 'signup') {
      return <SignUpScreen onSwitchToLogin={() => setAuthMode('login')} />;
    }
    return <LoginScreen onSwitchToSignUp={() => setAuthMode('signup')} />;
  }

  // Authenticated but no profile
  if (!hasProfile || !user) {
    return <ProfileSetupScreen />;
  }

  // Existing user missing phone — prompt them
  if (!user.hasPhone) {
    return <PhoneRequiredPrompt />;
  }

  // Fully authenticated with profile
  return (
    <AppProvider>
      <SplashScreen>
        <Routes>
          <Route path="/add/:handle" element={<AddFriendRedirect />} />
          <Route element={<AppShell />}>
            <Route path="/" element={<CalendarTab />} />
            <Route path="/overlaps" element={<Navigate to="/" replace />} />
            <Route path="/friends" element={<FriendsTab />} />
            <Route path="/messages" element={<MessagesTab />} />
            <Route path="/profile" element={<ProfileTab />} />
          </Route>
        </Routes>
      </SplashScreen>
    </AppProvider>
  );
}

function PhoneRequiredPrompt() {
  const { session, refreshProfile } = useAuth();
  const [countryCode, setCountryCode] = useState('971');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const digits = phoneDigits.replace(/\D/g, '');
    if (!digits || digits.length < 6 || digits.length > 15) {
      setError('Please enter a valid phone number (6-15 digits).');
      return;
    }
    const phoneValue = `+${countryCode}${digits}`;
    const e164 = normalizeToE164(phoneValue);
    if (!e164) {
      setError('Could not validate this phone number. Please check the format.');
      return;
    }
    setSaving(true);
    let phoneHash: string;
    try {
      phoneHash = await hashPhone(e164);
    } catch {
      setError('Failed to process phone number. Please try again.');
      setSaving(false);
      return;
    }
    saveLocalPhone(session!.user.id, e164);
    const { error: dbError } = await supabase.from('profiles').update({ phone_hash: phoneHash }).eq('id', session!.user.id);
    if (dbError) { setError(dbError.message); setSaving(false); return; }
    await refreshProfile();
    setSaving(false);
  };

  return (
    <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6">
      <div className="flex flex-col items-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
          <Phone size={32} className="text-accent" />
        </div>
        <h1 className="text-2xl font-bold text-slate-100">Phone Number Required</h1>
        <p className="text-slate-400 text-sm mt-2 text-center max-w-xs">
          We now require your phone number so crew members can find you through their contacts.
        </p>
      </div>
      <form onSubmit={handleSave} className="w-full max-w-sm space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">{error}</div>
        )}
        <div className="flex gap-2">
          <div className="relative shrink-0">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="w-[110px] px-3 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors appearance-none text-sm"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.dial} className="bg-slate-700 text-slate-100">
                  {c.flag} +{c.dial}
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
            />
          </div>
          <input
            type="tel"
            autoComplete="tel-national"
            value={phoneDigits}
            onChange={(e) => setPhoneDigits(e.target.value.replace(/[^\d\s]/g, ''))}
            placeholder="50 1234567"
            className="flex-1 px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 rounded-lg bg-accent text-white font-semibold text-base hover:bg-accent-dark active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

function SetNewPasswordScreen({
  updatePassword,
  onDone,
}: {
  updatePassword: (pw: string) => Promise<{ error: string | null }>;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    const result = await updatePassword(password);
    setSaving(false);
    if (result.error) {
      setError(result.error);
    } else {
      onDone();
    }
  };

  return (
    <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <h2 className="text-xl font-bold text-slate-100 mb-2">Set new password</h2>
        <p className="text-slate-400 text-sm mb-6">Choose a new password for your account.</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <input
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
          />
          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-lg bg-accent text-white font-semibold text-base hover:bg-accent-dark active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppRouter>
        <ThemeProvider>
          <AuthProvider>
            <DeviceFrame>
              <AuthGate />
            </DeviceFrame>
          </AuthProvider>
        </ThemeProvider>
      </AppRouter>
    </ErrorBoundary>
  );
}
